// Multi-purpose quotation modal. The `type` prop selects what is shown:
//   "quotation-Creation" - HTML preview of the quotation being created (rowData = invoiceData).
//   "termsAndCondition"  - full description of one item row (stateData = that row). The name does not match the content.
//   "itemDetails"        - editor for the Terms & Conditions text (saved to Redux via addTearmAndCondtion).
//   no type              - a customer's sent PDFs with search + WhatsApp "Re Send" (used by CrmQuotationList).
// Props: show, handleClose, rowData, type, stateData. Used by crmQuotationList.jsx and crmQuotationCreation.jsx.
import {
    Dialog,
    DialogContent,
    TextField,
    Box, Button,
    Paper, InputAdornment,
    Typography, Divider,
    TableContainer, Table, TableHead, TableCell, TableRow,
    TableBody, Grid
} from "@mui/material";
import React, { useState, useEffect } from "react";
import Slide from "@mui/material/Slide";
import { useDispatch, useSelector } from "react-redux";
import { sendWaConvoTemplateThunk } from "../../messages/messagesThunk";
import ReplayCircleFilledIcon from '@mui/icons-material/ReplayCircleFilled';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import GavelIcon from '@mui/icons-material/Gavel';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { QR, QrActionButton, QrModalHeader, qrDialogPaperSx } from "../../qrCode/components/qrUi";
import { nextBtnSx } from "../../campaign/components/stepUi";
import { addTearmAndCondtion } from "../quotationSlice";
import SnackbarMessage from "../../../components/snackBar/snackBar";
import touchLineLogo from '../../../assests/touchLineLogo.jpeg';
import redFlagLogo from '../../../assests/touchLineRedFlagLogo.jpg';

// Slide-down transition for the dialog.
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />;
});

export default function ViewQuotationModal({ show, handleClose, rowData, type, stateData }) {
    // PDF browser state (no-type mode): selected quotation no./URL, search text, and blob URLs keyed by quotation_no.
    const [selectedPdf, setSelectedPdf] = useState(null);
    const [selectedPdfUrl, setSelectedPdfUrl] = useState("");
    const [searchText, setSearchText] = useState("");
    const [pdfUrls, setPdfUrls] = useState({});
    const dispatch = useDispatch();

    const { quotationTemplates = [], tearmAndConditions = {} } = useSelector((state) => state.crmQuotation);
    const { status2, error } = useSelector((state) => state.messages);

    // Only set in the customer list mode (grouped row from CrmQuotationList).
    const { wa_contact_id } = rowData;

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    // Converts the stored HTML terms to plain text for editing in the textarea.
    const htmlToPlainText = (html) => {
        return html
            .replace(/<br\s*\/?>/gi, "\n")        // convert <br/> to newline
            .replace(/<p>/gi, "")                // remove <p>
            .replace(/<\/p>/gi, "\n\n")          // close </p> adds new paragraph
            .replace(/<b>/gi, "")                // remove <b>
            .replace(/<\/b>/gi, "")              // remove </b>
            .replace(/•/g, "• ")                 // keep bullet
            .trim();
    };


    // Editable terms text, initialised from the store when the modal mounts.
    const [terms, setTerms] = useState(htmlToPlainText(tearmAndConditions));
    const [errors, setErrors] = useState("");

    // When rowData changes: download each of the customer's PDFs, create local blob URLs for the <embed> viewer,
    // and select the first (newest) quotation. Does nothing when rowData has no quotations.
    useEffect(() => {
        const fetchAllPdfs = async () => {
            const urls = {};

            for (const q of rowData?.quotations || []) {
                if (q.quotation_path) {
                    const response = await fetch(q.quotation_path);
                    const blob = await response.blob();
                    urls[q.quotation_no] = URL.createObjectURL(blob);
                }
            }

            setPdfUrls(urls);

            if (rowData?.quotations?.length > 0) {
                setSelectedPdf(rowData.quotations[0].quotation_no);
                setSelectedPdfUrl(rowData.quotations[0]?.quotation_path);
            }
        };

        fetchAllPdfs();
    }, [rowData]);

    // Clears the search box when the modal closes.
    useEffect(() => {
        if (!show) {
            setSearchText("")
        }
    }, [show]);


    // Re-sends the selected PDF on WhatsApp with template 528 (same template as the create flow),
    // passing the PDF URL as replaceUrl, then closes the modal.
    const handleReSendQuotation = async (e) => {
        e.preventDefault();

        await dispatch(
            sendWaConvoTemplateThunk({
                credentials: {
                    receiver_contact_id: wa_contact_id,
                    template_id: 528,
                    variables: [],
                    replaceUrl: selectedPdfUrl
                }
            })
        );
        handleClose();
    };


    // "itemDetails" mode: validates the terms are not empty, saves them to Redux and closes.
    const handleSubmit = () => {
        if (!terms.trim()) {
            setErrors("Terms & Conditions cannot be empty");
            setSnackbar({
                open: true,
                message: "Terms & Conditions cannot be empty",
                severity: "error",
            });
            return;
        }

        setErrors("");

        dispatch(addTearmAndCondtion(terms));
        handleClose();

        setSnackbar({
            open: true,
            message: "Successfully Added",
            severity: "idle",
        });
    };

    // Parses "Key: value" lines of the terms into an object. The result appears to be used only for the console log.
    const parseTermsToObject = (termsText) => {
        const obj = {};
        const lines = termsText.split("\n").map(l => l.trim()).filter(Boolean);

        lines.forEach(line => {
            const [key, ...rest] = line.split(":");
            if (key && rest.length > 0) {
                obj[key.toLowerCase()] = rest.join(":").trim();
            }
        });

        return obj;
    };

    const displayTerms = parseTermsToObject(terms);
    console.log("displayTerms", displayTerms);


    return (
        <>
            <Dialog
                open={show}
                onClose={handleClose}
                TransitionComponent={Transition}
                fullWidth
                maxWidth={["quotation-Creation", "termsAndCondition", "itemDetails"].includes(type) ? "md" : "lg"}
                keepMounted
                PaperProps={{
                    sx: qrDialogPaperSx,
                }}
            >
                {/* Header */}
                <QrModalHeader
                    icon={type === "itemDetails" ? <GavelIcon /> : type === "termsAndCondition" ? <Inventory2OutlinedIcon /> : <RequestQuoteIcon />}
                    title={type === "itemDetails" ? "Terms && Conditions" : "View Quotation"}
                    subtitle={
                        type === "itemDetails" ? "Edit the terms printed on the quotation"
                            : type === "termsAndCondition" ? "Item description and specifications"
                                : type === "quotation-Creation" ? "Preview before sending"
                                    : rowData?.name ? `Quotations sent to ${rowData.name}` : undefined
                    }
                    onClose={handleClose}
                />

                {/* Content */}
                <DialogContent sx={{ p: { xs: 1.5, sm: 2.5 }, bgcolor: type === "quotation-Creation" ? "#f8fafc" : "#fff" }}>
                    {
                        type === "quotation-Creation" ? (
                            <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 900, margin: "0 auto", bgcolor: "#fff", border: `1px solid ${QR.border}`, borderRadius: 3, boxShadow: "0 1px 3px rgba(16,24,40,0.06)" }}>

                                {/* Mode "quotation-Creation": HTML preview mirroring the crmQuotationPDF layout. */}
                                <Grid container spacing={1}>
                                    <Grid item size={{ xs: 12, md: 3 }} mt={3}>
                                        <Box component="img"
                                            sx={{
                                                height: 100,
                                                width: 180,
                                                maxWidth: "100%",
                                                objectFit: "contain",
                                            }}
                                            alt="The house from the offer." src={touchLineLogo}></Box>
                                    </Grid>
                                    <Grid item size={{ xs: 12, md: 6 }}>
                                        <Typography
                                            variant="h5"
                                            sx={{ fontWeight: "bold", color: "red", textAlign: "center" }}
                                        >
                                            {rowData.companyName}
                                        </Typography>

                                        <Typography sx={{ textAlign: "center", fontSize: 14, mt: 1, wordBreak: "break-word" }}>
                                            {rowData.companyAddress}<br />
                                            Tel: {rowData.mobile1}, {rowData.mobile2}<br />
                                            Website: {rowData.website}<br />
                                            Email: {rowData.email}
                                        </Typography>
                                    </Grid>


                                    <Grid item size={{ xs: 12, md: 3 }}>
                                        <Box component="img"
                                            sx={{
                                                height: 150,
                                                width: 250,
                                                maxWidth: "100%",
                                                objectFit: "contain",
                                            }}
                                            alt="The house from the offer." src={redFlagLogo}></Box>
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 2 }} />

                                <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1, fontSize: 14, wordBreak: "break-word" }}>
                                    <Typography>Ref: {rowData.ref}</Typography>
                                    <Typography>Date: {rowData.date}</Typography>
                                </Box>

                                {/* */}

                                <Grid container spacing={1}>
                                    <Grid item size={{ xs: 12, md: 6 }}>
                                        <Box sx={{ mt: 1, whiteSpace: "normal", wordBreak: "break-word" }}>
                                            <Typography>To,</Typography>
                                            <Typography>{rowData.receiverName}</Typography>
                                            <Typography>{rowData.receiverCity}</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>

                                <Box sx={{ mt: 2 }}>
                                    <Typography>Dear Sir,</Typography>

                                    <Typography sx={{ fontWeight: "bold", mt: 1 }}>
                                        Sub: {rowData.subject}
                                    </Typography>
                                </Box>

                                <Typography sx={{ mt: 2 }}>
                                    Thank you very much for your enquiry for your requirement of TOUCH LINE Interactive Flat Panel.
                                    We are pleased to submit our best offer for the same for your kind consideration.
                                </Typography>

                                <TableContainer component={Paper} elevation={0} sx={{ mt: 3, borderRadius: 3, border: `1px solid ${QR.border}`, overflowX: "auto" }}>
                                    <Table size="small" aria-label="responsive table" sx={{ minWidth: 520, "& th": { fontWeight: 700, fontSize: 12.5, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4 } }}>
                                        <TableHead>
                                            <TableRow sx={{ background: "#f1f5f9", borderRadius: 5 }}>
                                                {
                                                    [{ value: 1, label: "S.No" }, { value: 2, label: "Item & Size" }, { value: 3, label: "Qty" }, { value: 4, label: "Rate" }, { value: 4, label: "Ammount" }].map((item, index) => (
                                                        <TableCell key={index}>{item.label}</TableCell>
                                                    ))
                                                }
                                            </TableRow>
                                        </TableHead>

                                        <TableBody sx={{ borderRadius: 3 }}>

                                            {rowData.items.map((item, idx) => (
                                                <TableRow key={idx} sx={{
                                                    "& td, & th": { border: 0 }  // removes cell borders
                                                }}>
                                                    <TableCell sx={{ width: "60px" }}>{idx + 1}</TableCell>

                                                    <TableCell sx={{ width: "40%" }}>
                                                        <Typography sx={{ fontWeight: "bold", whiteSpace: "pre-line" }}>
                                                            {item.title.split("•")[0].trim() || ""}
                                                        </Typography>
                                                    </TableCell>

                                                    <TableCell>{item.qty}</TableCell>

                                                    <TableCell>
                                                        ₹ {Number(item.rate).toLocaleString("en-IN", {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2
                                                        })}
                                                    </TableCell>

                                                    <TableCell>
                                                        ₹ {Number(item.ammount).toLocaleString("en-IN", {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2
                                                        })}
                                                    </TableCell>
                                                </TableRow>
                                            ))}

                                            <TableRow sx={{ background: `${QR.main}0f`, borderTop: `1px solid ${QR.border}` }}>
                                                <TableCell colSpan={2} sx={{ fontWeight: "bold" }}>
                                                    TOTAL
                                                </TableCell>

                                                <TableCell sx={{ fontWeight: "bold", color: QR.main }}>
                                                    {rowData.items.reduce((sum, item) => sum + Number(item.qty), 0)}
                                                </TableCell>

                                                <TableCell sx={{ fontWeight: "bold" }}>
                                                    ₹ {
                                                        rowData.items
                                                            .reduce((sum, item) => sum + Number(item.rate), 0)
                                                            .toLocaleString("en-IN", {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2
                                                            })
                                                    }
                                                </TableCell>

                                                <TableCell sx={{ fontWeight: "bold" }}>
                                                    ₹ {
                                                        rowData.items
                                                            .reduce((sum, item) => sum + Number(item.ammount), 0)
                                                            .toLocaleString("en-IN", {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2
                                                            })
                                                    }
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <Box mt={2}>
                                    {/* Bullet features of the FIRST template only (index 0), same as the PDF. */}
                                    {quotationTemplates.map((item, index) => (
                                        <Typography fontWeight="bold" key={index}>
                                            {index === 0 &&
                                                item.item_desc.split("•").map((text, i) =>
                                                    i !== 0 ? (
                                                        <span key={i}>
                                                            <br />•{text}
                                                        </span>
                                                    ) : null
                                                )}
                                        </Typography>
                                    ))}
                                </Box>

                                <Box sx={{ mt: 1, fontSize: 14, lineHeight: 1.8, mt: 0 }}>
                                    <Typography component="div" sx={{ fontSize: 14, lineHeight: 1.7 }}>
                                        {/* Terms come from the store (HTML string); newlines become <br/>. */}
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: tearmAndConditions.replace(/\n/g, "<br/>"),
                                            }}
                                        />
                                    </Typography>
                                </Box>

                                <Box sx={{ mt: 2 }}>
                                    <Typography>Yours Truly,</Typography>
                                    <Typography sx={{ fontWeight: "bold" }}>For {rowData.companyName},</Typography>

                                    <Typography sx={{ mt: 0 }}>Sd/-</Typography>

                                    <Typography sx={{ fontWeight: "bold" }}>
                                        {rowData.signedBy}
                                    </Typography>

                                    <Typography>{rowData.mobile1}</Typography>
                                </Box>
                            </Box>
                        ) : type === "termsAndCondition" ? (
                            <>
                                {/* Mode "termsAndCondition": description of the selected row's item, one bullet per line. */}
                                <Typography sx={{ whiteSpace: "pre-line", fontSize: 14, lineHeight: 1.75, color: "#334155", p: { xs: 1.5, sm: 2 }, borderRadius: 3, bgcolor: "#f8fafc", border: `1px solid ${QR.border}` }}>
                                    {
                                        quotationTemplates.find(t => Number(t.id) === Number(stateData?.item))
                                            ?.item_desc.replace(/•/g, "\n•") || "No description available"
                                    }
                                </Typography>
                            </>
                        ) : type === "itemDetails" ? (
                            <Box sx={{ pt: 1 }}>
                                {/* Mode "itemDetails": Terms & Conditions editor; Update saves to Redux. */}
                                <Grid container spacing={2}>
                                    <Grid item size={{ xs: 12, md: 12 }}>
                                        <TextField
                                            fullWidth
                                            label="Terms & Conditions"
                                            multiline
                                            rows={8}
                                            value={terms}
                                            error={!!errors}
                                            onChange={(e) => { setTerms(e.target.value); setErrors(""); }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 2.5,
                                                    fontSize: 14,
                                                    "& .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: "#e5e7eb" },
                                                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: QR.blue },
                                                    "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "#ef4444" },
                                                },
                                                "& .MuiInputLabel-root.Mui-focused": { color: QR.blue },
                                            }}
                                        />
                                    </Grid>

                                    <Grid item size={{ xs: 12, md: 12 }} sx={{ display: "flex", justifyContent: "flex-end", pt: 1.5, borderTop: `1px solid ${QR.border}` }}>
                                        <Button
                                            variant="contained"
                                            onClick={handleSubmit}
                                            sx={{ ...nextBtnSx, width: { xs: "100%", sm: "auto" } }}
                                        >
                                            Update
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", md: "280px minmax(0, 1fr)" },
                                    gap: 2,
                                    minHeight: { md: "75vh" },
                                }}
                            >
                                {/* Default mode (no type): customer's sent PDFs - list on the left, viewer on the right. */}
                                {/* Left: quotation numbers */}
                                <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, p: 1.5, borderRadius: 3, bgcolor: "#f8fafc", border: `1px solid ${QR.border}` }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.6, mb: 1 }}>
                                        Quotations ({rowData?.quotations?.length || 0})
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Search quotation no..."
                                        value={searchText}
                                        inputProps={{ maxLength: 20 }}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value.startsWith(" ")) return;
                                            setSearchText(value);
                                        }}
                                        variant="outlined"
                                        sx={{
                                            mb: 1.5,
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 2.5,
                                                bgcolor: "#fff",
                                                fontSize: 13.5,
                                                "& .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: "#e5e7eb" },
                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: QR.blue },
                                            },
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start" sx={{ "& svg": { fontSize: 18, color: "#94a3b8" } }}>
                                                    <SearchIcon />
                                                    <Box component="span" sx={{ ml: 0.75, fontSize: 12.5, color: "#64748b" }}>SB / Quote /</Box>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Box sx={{ pr: 0.5, maxHeight: { xs: 200, md: "65vh" }, overflowY: "auto", display: "flex", flexDirection: "column", gap: 0.75 }}>
                                        {/* Filtered by quotation number as the user types; clicking selects that PDF. */}
                                        {rowData?.quotations
                                            ?.filter((q) =>
                                                q.quotation_no.toString().toLowerCase()
                                                    .includes(searchText.toLowerCase())
                                            ).map((item, index) => {
                                                const active = selectedPdf === item.quotation_no;
                                                return (
                                                    <Box
                                                        key={index}
                                                        onClick={() => { setSelectedPdf(item.quotation_no); setSelectedPdfUrl(item?.quotation_path); }}
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                            px: 1.25,
                                                            py: 1,
                                                            borderRadius: 2.5,
                                                            cursor: "pointer",
                                                            fontWeight: 600,
                                                            fontSize: 13.5,
                                                            color: active ? QR.blue : "#334155",
                                                            bgcolor: active ? `${QR.blue}0f` : "#fff",
                                                            border: `1.5px solid ${active ? QR.blue : QR.border}`,
                                                            transition: "all .15s ease",
                                                            "&:hover": { borderColor: QR.blue, color: QR.blue },
                                                        }}
                                                    >
                                                        <PictureAsPdfOutlinedIcon sx={{ fontSize: 18, color: active ? QR.blue : "#dc2626" }} />
                                                        <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                            {item.quotation_no}
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                    </Box>
                                </Box>

                                {/* Right: selected PDF */}
                                <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", p: 1.5, borderRadius: 3, bgcolor: "#fff", border: `1px solid ${QR.border}` }}>
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.25, mb: 1.5 }}>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                                                Selected Quotation
                                            </Typography>
                                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a", wordBreak: "break-all" }}>
                                                {selectedPdf}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ width: { xs: "100%", sm: "auto" }, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } } }}>
                                            <QrActionButton
                                                variant="solid"
                                                color={QR.blue}
                                                gradientTo="#3b82f6"
                                                icon={<ReplayCircleFilledIcon sx={{ fontSize: 17 }} />}
                                                label="Re Send"
                                                loading={status2 === "loading"}
                                                loadingLabel="Sending.."
                                                onClick={handleReSendQuotation}
                                            />
                                        </Box>
                                    </Box>

                                    {/* Shows the locally created blob URL of the selected quotation. */}
                                    <embed
                                        src={pdfUrls[selectedPdf]}
                                        style={{
                                            width: "100%",
                                            height: "70vh",
                                            borderRadius: "12px",
                                            border: `1px solid ${QR.border}`,
                                        }}
                                        type="application/pdf"
                                    />
                                </Box>
                            </Box>
                        )
                    }
                </DialogContent>

                <SnackbarMessage
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                />
            </Dialog>
        </>
    );
}